import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 长按菜单（iOS 的 Context Menu 那种）。
 *
 * 长按是移动端唯一的"右键"，而 Web 上默认没有。自己做的关键在手势那一层
 * （见 hooks/gestures.ts 的三件套），这里负责定位与呈现：
 * - 菜单要出现在**手指附近**，不是屏幕中央——不然用户得把手移过去；
 * - 但不能被屏幕边缘裁掉，也不能盖住被长按的那个元素；
 * - 破坏性项放最后并标红，且和其它项之间要有间隔，防止误触。
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useOverlay, useRefNode } from '../hooks/overlay.js';
const MARGIN = 12;
const ESTIMATED_ITEM_H = 46;
export function ActionMenu({ open, onClose, anchor, items, title }) {
    const menuRef = useRef(null);
    const [pos, setPos] = useState(null);
    useOverlay({ active: open, onClose, containerRef: menuRef });
    // 先按估算摆一次（避免第一帧闪在左上角），挂载后用真实尺寸修正
    useEffect(() => {
        if (!open || !anchor)
            return;
        const w = 220;
        const h = items.length * ESTIMATED_ITEM_H + (title ? 34 : 0) + 12;
        setPos(clamp(anchor.x, anchor.y, w, h));
    }, [open, anchor, items.length, title]);
    // ⚠️ 依赖里必须有**真实节点**，不能只有 [open, anchor]。
    // open 翻转那一帧 pos 还是 null → 组件返回 null → menuRef.current 也是 null → 这里直接退出；
    // 而 [open, anchor] 之后再没变过，所以修正**永远不会跑**，菜单一直按 220px 的估算摆位，
    // 长标签或带图标列的菜单会被视口边缘裁掉——正是这一趟存在的理由。
    const menuNode = useRefNode(menuRef);
    useLayoutEffect(() => {
        if (!open || !anchor || !menuNode)
            return;
        const rect = menuNode.getBoundingClientRect();
        const next = clamp(anchor.x, anchor.y, rect.width, rect.height);
        // 值相等就不写回：否则每次修正都产生新对象，state 变化 → 重渲染 → 再修正，自激
        setPos((prev) => (prev && prev.left === next.left && prev.top === next.top ? prev : next));
    }, [open, anchor, menuNode]);
    if (!open || !anchor || !pos)
        return null;
    return (_jsxs("div", { className: "ps-menu-root", role: "presentation", children: [_jsx("div", { className: "ps-scrim ps-scrim-light", onClick: onClose, "aria-hidden": "true" }), _jsxs("div", { ref: menuRef, className: "ps-menu", role: "menu", "aria-label": title ?? '操作', tabIndex: -1, style: { left: pos.left, top: pos.top }, children: [title && _jsx("div", { className: "ps-menu-title", children: title }), items.map((item, i) => (_jsxs("button", { type: "button", role: "menuitem", disabled: item.disabled, className: `ps-menu-item ${item.destructive ? 'ps-danger' : ''} ${
                        // 破坏性项与上一项之间留一条分隔，防止手指顺势点下去
                        item.destructive && i > 0 && !items[i - 1]?.destructive ? 'ps-menu-sep' : ''}`.trim(), onClick: () => {
                            onClose();
                            item.onSelect();
                        }, children: [item.icon && _jsx("span", { className: "ps-menu-icon", children: item.icon }), _jsx("span", { children: item.label })] }, item.key)))] })] }));
}
/** 让菜单出现在手指附近，但不越出屏幕、不盖住手指 */
function clamp(x, y, w, h) {
    const vw = typeof window === 'undefined' ? 390 : window.innerWidth;
    const vh = typeof window === 'undefined' ? 800 : window.innerHeight;
    const left = Math.min(Math.max(MARGIN, x - w / 2), vw - w - MARGIN);
    // 优先放在手指下方；下面放不下就翻到上面（而不是硬挤，硬挤会盖住被长按的内容）
    const below = y + MARGIN;
    const top = below + h + MARGIN <= vh ? below : Math.max(MARGIN, y - h - MARGIN);
    return { left, top };
}
//# sourceMappingURL=action-menu.js.map